/*
  E-Gifts / E-Cards landing heroes. Loaded by sections/lp-a-landing.liquid only
  when there is something to animate. Vanilla JS, no dependencies. Without it
  both heroes simply show their first, static state.

  E-Gifts: a small stack of photos that rotates every 4.5 seconds.
  - Rotation holds while the pointer or keyboard focus is on the photos or dots.
  - Choosing a dot shows that photo and stops the rotation for good.
  - Under prefers-reduced-motion it never rotates on its own.
  - Only the front photo's link is focusable; the others are aria-hidden.

  Hero button: a gentle (900ms, ease-in-out) scroll to the gift grid that
  clears the sticky header, then focus moves to the grid heading. Instant
  under prefers-reduced-motion; the href (#lp-a-grid) still works without JS.

  E-Cards, phones only: the two pile cards above the email crossfade through
  every pile photo, staggered, never showing the same animal twice at once.
  Static under prefers-reduced-motion; pauses off-screen or in a hidden tab.

  E-Cards: the example email types its way through the examples in the data
  snippet (recipient, sender, amount, message), pausing a few seconds on each.
  - The animated text is aria-hidden; a visually hidden sentence carries the
    first example for screen readers.
  - Under prefers-reduced-motion it stays on the first example.
  - It pauses while the email is off-screen or the tab is hidden.
*/
(function () {
  'use strict';

  var DELAY = 4500;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    var stack = root.querySelector('[data-lp-a-stack]');
    if (!stack || stack.__lpA) return;
    stack.__lpA = true;

    var items = Array.prototype.slice.call(stack.querySelectorAll('.lp-a-stack__item'));
    if (items.length < 2) return;

    var dotsWrap = root.querySelector('[data-lp-a-dots]');
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-lp-a-dot]')) : [];
    var front = 0;
    var timer = null;
    var stopped = reduceMotion;
    var held = false;

    if (dotsWrap) dotsWrap.hidden = false;

    function render() {
      items.forEach(function (item, i) {
        var pos = (i - front + items.length) % items.length;
        item.setAttribute('data-pos', pos);
        item.setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
        var link = item.querySelector('a');
        if (link) {
          if (pos === 0) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        }
      });
      dots.forEach(function (dot, i) {
        if (i === front) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function go(i) {
      front = (i + items.length) % items.length;
      render();
    }

    function pause() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function play() {
      pause();
      if (stopped || held || document.hidden) return;
      timer = window.setInterval(function () { go(front + 1); }, DELAY);
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        stopped = true;
        pause();
        go(parseInt(dot.getAttribute('data-lp-a-dot'), 10));
      });
    });

    [stack, dotsWrap].forEach(function (el) {
      if (!el) return;
      el.addEventListener('mouseenter', function () { held = true; pause(); });
      el.addEventListener('mouseleave', function () { held = false; play(); });
      el.addEventListener('focusin', function () { held = true; pause(); });
      el.addEventListener('focusout', function () { held = false; play(); });
    });
    document.addEventListener('visibilitychange', play);

    render();
    play();
  }

  function initTyper(root) {
    var mock = root.querySelector('[data-lp-a-mock]');
    if (!mock || mock.__lpA || reduceMotion) return;
    mock.__lpA = true;

    var source = mock.querySelector('[data-lp-a-examples]');
    var examples;
    try { examples = JSON.parse(source.textContent); } catch (e) { return; }
    if (!examples || examples.length < 2) return;

    var KEYS = ['to', 'from', 'amount', 'message'];
    var slots = {};
    for (var k = 0; k < KEYS.length; k++) {
      slots[KEYS[k]] = mock.querySelector('[data-lp-a-slot="' + KEYS[k] + '"]');
      if (!slots[KEYS[k]]) return;
    }

    var onScreen = true;
    var waiting = [];

    function ready() { return onScreen && !document.hidden; }
    function wake() {
      if (!ready()) return;
      var queue = waiting;
      waiting = [];
      queue.forEach(function (resolve) { resolve(); });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        wake();
      }).observe(mock);
    }
    document.addEventListener('visibilitychange', wake);

    // Wait ms, then (if needed) until the email is on screen in a visible tab.
    function hold(ms) {
      return new Promise(function (resolve) { window.setTimeout(resolve, ms); }).then(function () {
        if (ready()) return;
        return new Promise(function (resolve) { waiting.push(resolve); });
      });
    }

    function retype(el, text, speed) {
      el.classList.add('is-typing');
      function erase() {
        if (!el.textContent.length) return type(0);
        el.textContent = el.textContent.slice(0, -1);
        return hold(32).then(erase);
      }
      function type(i) {
        if (i >= text.length) {
          return hold(200).then(function () { el.classList.remove('is-typing'); });
        }
        el.textContent = text.slice(0, i + 1);
        return hold(speed).then(function () { return type(i + 1); });
      }
      return erase();
    }

    var current = 0;
    function next() {
      current = (current + 1) % examples.length;
      var example = examples[current];
      var chain = Promise.resolve();
      KEYS.forEach(function (key) {
        chain = chain.then(function () {
          return retype(slots[key], String(example[key] || ''), key === 'message' ? 55 : 80);
        });
      });
      return chain.then(function () { return hold(3200); }).then(next);
    }

    hold(3500).then(next);
  }

  /* ---------- Shared: wait for a timer, then until el is visible ---------- */
  function makeGate(el, extra) {
    var onScreen = true;
    var waiting = [];
    function ready() { return onScreen && !document.hidden && (!extra || extra()); }
    function wake() {
      if (!ready()) return;
      var queue = waiting;
      waiting = [];
      queue.forEach(function (resolve) { resolve(); });
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        wake();
      }).observe(el);
    }
    document.addEventListener('visibilitychange', wake);
    return {
      wake: wake,
      hold: function (ms) {
        return new Promise(function (resolve) { window.setTimeout(resolve, ms); }).then(function () {
          if (ready()) return;
          return new Promise(function (resolve) { waiting.push(resolve); });
        });
      }
    };
  }

  /* ---------- Hero button: gentle scroll to the grid ---------- */
  function stickyOffset() {
    var styles = window.getComputedStyle(document.documentElement);
    function num(name) { return parseFloat(styles.getPropertyValue(name)) || 0; }
    // Same sum the theme uses (theme.js): sticky header + sticky announcement bar.
    var offset = num('--header-height') * num('--enable-sticky-header') +
      num('--announcement-bar-height') * num('--enable-sticky-announcement-bar');
    if (!offset) {
      var header = document.querySelector('.shopify-section--header');
      if (header && /sticky|fixed/.test(window.getComputedStyle(header).position)) {
        offset = header.getBoundingClientRect().height;
      }
    }
    return offset;
  }

  function easeInOut(p) {
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
  }

  function scrollToY(to, done) {
    var from = window.pageYOffset;
    var distance = to - from;
    function jump(y) {
      try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, y); }
    }
    if (reduceMotion || Math.abs(distance) < 2) {
      jump(to);
      done();
      return;
    }
    var DURATION = 900;
    var start = null;
    var cancelled = false;
    var stopEvents = ['wheel', 'touchstart', 'keydown'];
    function cancel() { cancelled = true; cleanup(); }
    function cleanup() {
      stopEvents.forEach(function (name) { window.removeEventListener(name, cancel); });
    }
    stopEvents.forEach(function (name) { window.addEventListener(name, cancel, { passive: true }); });
    function step(now) {
      if (cancelled) return;
      if (start === null) start = now;
      var progress = Math.min(1, (now - start) / DURATION);
      jump(from + distance * easeInOut(progress));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        cleanup();
        done();
      }
    }
    window.requestAnimationFrame(step);
  }

  function initScroll(root) {
    Array.prototype.forEach.call(root.querySelectorAll('a[data-lp-a-scroll]'), function (link) {
      if (link.__lpA) return;
      link.__lpA = true;
      link.addEventListener('click', function (event) {
        var id = (link.getAttribute('href') || '').replace(/^#/, '');
        var section = id && document.getElementById(id);
        if (!section) return;
        event.preventDefault();
        var target = section.querySelector('h2[tabindex="-1"]') || section;
        var to = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset() - 24;
        if (window.history && window.history.pushState) window.history.pushState(null, '', '#' + id);
        scrollToY(Math.max(0, to), function () {
          try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
        });
      });
    });
  }

  /* ---------- E-Cards, phones: crossfade the two visible pile cards ---------- */
  function initPileCycle(root) {
    var mock = root.querySelector('[data-lp-a-mock]');
    if (!mock || mock.__lpAPile || reduceMotion || !window.matchMedia) return;
    mock.__lpAPile = true;

    var source = mock.querySelector('[data-lp-a-pile]');
    var pool;
    try { pool = JSON.parse(source.textContent); } catch (e) { return; }
    if (!pool || pool.length < 3) return;

    var slots = [mock.querySelector('.lp-a-mock__fan--1'), mock.querySelector('.lp-a-mock__fan--2')];
    if (!slots[0] || !slots[1]) return;

    var phone = window.matchMedia('(max-width: 699px)');
    var gate = makeGate(mock, function () { return phone.matches; });
    var original = slots.map(function (slot) { return slot.innerHTML; });
    var visible = [[0], [1]]; // pool indexes on screen in each slot (two while fading)
    var pointer = 2;
    var FADE = 700;
    var HOLD = 2500;

    function reset() {
      if (phone.matches) return gate.wake();
      slots.forEach(function (slot, i) { slot.innerHTML = original[i]; });
      visible = [[0], [1]];
    }
    if (phone.addEventListener) phone.addEventListener('change', reset);
    else if (phone.addListener) phone.addListener(reset);

    function pick(s) {
      var taken = visible[0].concat(visible[1]);
      for (var tries = 0; tries < pool.length; tries++) {
        var i = pointer % pool.length;
        pointer++;
        if (taken.indexOf(i) === -1) return i;
      }
      return -1;
    }

    function layerFor(item) {
      var layer = document.createElement('div');
      layer.className = 'lp-a-mock__layer';
      layer.style.opacity = '0';
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.sizes = '55vw';
      img.srcset = item.srcset;
      img.src = item.src;
      img.width = 1200;
      img.height = 800;
      if (item.focus) img.style.objectPosition = item.focus;
      layer.appendChild(img);
      if (item.credit) {
        var credit = document.createElement('span');
        credit.className = 'lp-a-mock__fan-credit';
        credit.textContent = item.credit;
        layer.appendChild(credit);
      }
      return layer;
    }

    function loaded(img) {
      if (img.decode) return img.decode().catch(function () {});
      return new Promise(function (resolve) {
        if (img.complete) return resolve();
        img.onload = img.onerror = function () { resolve(); };
      });
    }

    function cycle(s, wait) {
      return gate.hold(wait).then(function () {
        var i = pick(s);
        if (i === -1) return cycle(s, HOLD);
        var slot = slots[s];
        var layer = layerFor(pool[i]);
        visible[s] = visible[s].concat([i]);
        slot.appendChild(layer);
        return loaded(layer.querySelector('img')).then(function () {
          return gate.hold(0);
        }).then(function () {
          void layer.offsetWidth; // start the transition from opacity 0
          layer.style.opacity = '1';
          return gate.hold(FADE + 50);
        }).then(function () {
          Array.prototype.forEach.call(slot.querySelectorAll('.lp-a-mock__layer'), function (old) {
            if (old !== layer) old.parentNode.removeChild(old);
          });
          visible[s] = [i];
          return cycle(s, HOLD);
        });
      });
    }

    // Stagger the two cards by about half a cycle.
    cycle(0, HOLD);
    cycle(1, HOLD + Math.round((HOLD + FADE) / 2));
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.lp-a'), function (root) {
      initScroll(root);
      init(root);
      initTyper(root);
      initPileCycle(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
